import { useMemo } from 'react';
import { Card, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import {
  type AntiDetectionSignalSpec,
  correlationColor,
  correlationBand,
} from '../../shared/anti-detection/signals';

const { Text } = Typography;

/**
 * Sprint 25 / S25-M — Dual-signal hourly overlay sparkline for the
 * heatmap cell drill-down. Two stacked SVG line charts on the same 24h
 * axis so spike alignment is visually obvious.
 *
 * Why two stacked plots instead of dual-Y on one plot:
 *   Spike *timing* alignment is the property the matrix flags via Pearson;
 *   dual-Y would tempt the viewer to over-read scale similarity. Stacked
 *   plots make timing the primary visual signal.
 */
export interface DualSignalOverlayProps {
  row: AntiDetectionSignalSpec;
  col: AntiDetectionSignalSpec;
  hourBuckets: string[];
  rowSeries: number[];
  colSeries: number[];
  r: number;
  strong: boolean;
}

const WIDTH = 720;
const HEIGHT_EACH = 96;
const PADDING_X = 36;
const PADDING_Y = 14;

function makePath(series: number[], height: number): string {
  if (series.length === 0) return '';
  const max = Math.max(1, ...series);
  const innerW = WIDTH - 2 * PADDING_X;
  const innerH = height - 2 * PADDING_Y;
  const step = innerW / Math.max(1, series.length - 1);
  return series
    .map((v, i) => {
      const x = PADDING_X + i * step;
      const y = PADDING_Y + innerH - (v / max) * innerH;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

function maxValue(series: number[]): number {
  return series.length === 0 ? 0 : Math.max(...series);
}

function sumValue(series: number[]): number {
  return series.reduce((a, b) => a + b, 0);
}

export const DualSignalOverlay: React.FC<DualSignalOverlayProps> = ({
  row,
  col,
  hourBuckets,
  rowSeries,
  colSeries,
  r,
  strong,
}) => {
  const rowPath = useMemo(() => makePath(rowSeries, HEIGHT_EACH), [rowSeries]);
  const colPath = useMemo(() => makePath(colSeries, HEIGHT_EACH), [colSeries]);

  const rowColor = correlationColor(r > 0 ? 0.8 : 0.45);
  const colColor = correlationColor(r > 0 ? 0.6 : -0.85);

  const xTicks = useMemo(() => {
    if (hourBuckets.length === 0) return [];
    const innerW = WIDTH - 2 * PADDING_X;
    const step = innerW / Math.max(1, hourBuckets.length - 1);
    // Show every 4 hours
    return hourBuckets
      .map((iso, i) => ({
        x: PADDING_X + i * step,
        label: dayjs(iso).format('HH:00'),
        index: i,
      }))
      .filter(({ index }) => index % 4 === 0);
  }, [hourBuckets]);

  return (
    <Card
      data-testid="dual-signal-overlay"
      title={
        <Space size={12} wrap>
          <span>{row.label}</span>
          <Text type="secondary">×</Text>
          <span>{col.label}</span>
          <Tag
            color={strong ? 'red' : 'default'}
            style={{ marginLeft: 4 }}
            data-testid="dual-signal-r-badge"
          >
            r = {r.toFixed(3)} · {correlationBand(r)}
          </Tag>
        </Space>
      }
      style={{ marginTop: 16 }}
    >
      <svg
        data-testid="dual-signal-overlay-svg"
        width="100%"
        viewBox={`0 0 ${WIDTH} ${HEIGHT_EACH * 2 + 28}`}
      >
        {/* Top track — row signal */}
        <g>
          <text x={4} y={PADDING_Y + 4} fontSize={11} fill={rowColor}>
            {row.label}
          </text>
          <text
            x={WIDTH - 4}
            y={PADDING_Y + 4}
            fontSize={11}
            fill="currentColor"
            textAnchor="end"
            opacity={0.6}
          >
            peak {maxValue(rowSeries)} / total {sumValue(rowSeries)}
          </text>
          <path
            data-testid="row-series-path"
            d={rowPath}
            fill="none"
            stroke={rowColor}
            strokeWidth={1.5}
          />
          <line
            x1={PADDING_X}
            y1={HEIGHT_EACH - PADDING_Y}
            x2={WIDTH - PADDING_X}
            y2={HEIGHT_EACH - PADDING_Y}
            stroke="currentColor"
            strokeOpacity={0.15}
          />
        </g>
        {/* Bottom track — col signal */}
        <g transform={`translate(0, ${HEIGHT_EACH + 28})`}>
          <text x={4} y={PADDING_Y + 4} fontSize={11} fill={colColor}>
            {col.label}
          </text>
          <text
            x={WIDTH - 4}
            y={PADDING_Y + 4}
            fontSize={11}
            fill="currentColor"
            textAnchor="end"
            opacity={0.6}
          >
            peak {maxValue(colSeries)} / total {sumValue(colSeries)}
          </text>
          <path
            data-testid="col-series-path"
            d={colPath}
            fill="none"
            stroke={colColor}
            strokeWidth={1.5}
          />
          <line
            x1={PADDING_X}
            y1={HEIGHT_EACH - PADDING_Y}
            x2={WIDTH - PADDING_X}
            y2={HEIGHT_EACH - PADDING_Y}
            stroke="currentColor"
            strokeOpacity={0.15}
          />
        </g>
        {/* X-axis labels at the bottom of the bottom track */}
        <g
          transform={`translate(0, ${HEIGHT_EACH * 2 + 28 - PADDING_Y + 6})`}
        >
          {xTicks.map((t) => (
            <text
              key={t.index}
              x={t.x}
              y={0}
              fontSize={10}
              fill="currentColor"
              opacity={0.5}
              textAnchor="middle"
            >
              {t.label}
            </text>
          ))}
        </g>
      </svg>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Pearson r computed over 24 hour-buckets. Stacked tracks share the X
        axis so spike-timing alignment is the primary visual signal.
      </Text>
    </Card>
  );
};
